import { Theme } from "@emotion/react";
import { mdsCommonTheme } from "../common";

export const mdsHarvestThemeColors = {
  ink: {
    0: "#D6D5E6",
    100: "#A5A3D0",
    200: "#7977BA",
    300: "#413F8E",
    400: "#100F31",
  },
  inkDark: {
    0: "#11111A",
    50: "#1A1925",
    100: "#20202F",
    150: "#272638",
    200: "#2E2D41",
    300: "#3C3B54",
    400: "#4C4B67",
    500: "#504F6D",
    600: "#89889F",
    700: "#D6D6E6",
  },
  graphite: {
    0: "#FEFEFE",
    100: "#F6F6F6",
    200: "#E9E9E9",
    300: "#DDDDDD",
    400: "#CECECE",
    500: "#BDBDBD",
    600: "#9C9C9C",
    700: "#7A7A7A",
    800: "#595959",
    900: "#373737",
  },
  canvas: {
    0: "#FCF8F1",
    100: "#F9F1E4",
  },
  blueprint: {
    0: "#F6F8FF",
    100: "#E6EDFF",
    200: "#A6BFFF",
    300: "#6792FF",
    400: "#3D69D9",
    500: "#1D47B3",
  },
  latte: {
    0: "#F9F1E2",
    100: "#F1E7D3",
    200: "#E8DCC4",
    300: "#E4D6B9",
  },
  editor: {
    0: "#FFE8E6",
    100: "#FFB5AB",
    200: "#FF8170",
    300: "#FF5C45",
    400: "#FF361A",
    500: "#DB1A00",
  },
  editorDark: {
    0: "#3F1A1F",
    100: "#5F272F",
    200: "#8E404B",
    300: "#B8626F",
    400: "#D9818E",
  },
  successLight: "#D8F8EE",
  transparentCanvas: "rgba(252, 248, 241, 0.25)",
  transparentInk: "rgba(39, 38, 56, 0.5)",
};

export const mdsThemeHarvest: Theme = {
  ...mdsCommonTheme,

  colors: {
    ...mdsCommonTheme.colors,
    ...mdsHarvestThemeColors,

    text: {
      contrast: mdsHarvestThemeColors.ink[400],
      danger: mdsHarvestThemeColors.editor[500],
      primary: mdsHarvestThemeColors.graphite[900],
      secondary: mdsHarvestThemeColors.graphite[800],
      tertiary: mdsHarvestThemeColors.graphite[600],
      onAccent: mdsHarvestThemeColors.graphite[0],
      link: {
        default: mdsHarvestThemeColors.blueprint[400],
        hover: mdsHarvestThemeColors.blueprint[500],
      },
      emphasis: mdsHarvestThemeColors.blueprint[400],
      positive: mdsCommonTheme.colors.green.x400, // TBD
      negative: mdsHarvestThemeColors.editor[400],
      editor: {
        metadata: mdsHarvestThemeColors.graphite[800],
      },
    },
    icon: {
      primary: mdsHarvestThemeColors.graphite[800],
      secondary: mdsHarvestThemeColors.graphite[700],
      placeholder: mdsHarvestThemeColors.graphite[600],
      onAccent: mdsHarvestThemeColors.graphite[0],
      brand: mdsHarvestThemeColors.editor[300],
      selected: mdsHarvestThemeColors.blueprint[300],
      emphasis: mdsHarvestThemeColors.blueprint[300],
      danger: mdsHarvestThemeColors.editor[400],
      onChromeInactive: mdsHarvestThemeColors.latte[300],
    },
    surface: {
      chrome: mdsHarvestThemeColors.canvas[100],
      pane: mdsHarvestThemeColors.graphite[0],
      paneHeader: mdsHarvestThemeColors.graphite[100],
      headsUp: {
        default: mdsHarvestThemeColors.graphite[100],
        hover: mdsHarvestThemeColors.graphite[200],
      },
      chat: mdsHarvestThemeColors.graphite[200],
      menu: mdsHarvestThemeColors.graphite[0],
      modal: mdsHarvestThemeColors.graphite[0],
      popover: mdsHarvestThemeColors.graphite[0],
      toast: mdsHarvestThemeColors.graphite[0],
      preview: mdsHarvestThemeColors.graphite[0],
      previewHeader: mdsHarvestThemeColors.graphite[100],
      tooltip: mdsHarvestThemeColors.graphite[800],
      overlay: {
        default: "rgba(0, 0, 0, 0.5)",
        onboarding: mdsHarvestThemeColors.transparentCanvas,
      },
      onboarding: mdsHarvestThemeColors.latte[100],
      onboardingSubtle: mdsHarvestThemeColors.canvas[0],
    },
    border: {
      input: {
        default: {
          default: mdsHarvestThemeColors.graphite[400],
          focused: mdsHarvestThemeColors.graphite[500],
          danger: mdsHarvestThemeColors.editor[400],
        },
        brand: {
          default: mdsCommonTheme.colors.transparent,
          focused: mdsHarvestThemeColors.editor[300],
        },
        emphasis: {
          default: mdsHarvestThemeColors.graphite[400],
          focused: mdsHarvestThemeColors.blueprint[200],
        },
      },
      button: {
        marketing: {
          default: mdsHarvestThemeColors.ink[400],
          hover: mdsHarvestThemeColors.editor[300],
        },
      },
      default: mdsHarvestThemeColors.graphite[200],
      hover: mdsHarvestThemeColors.graphite[300],
      onAccent: mdsHarvestThemeColors.graphite[300],
      chrome: mdsHarvestThemeColors.canvas[100],
      emphasis: mdsHarvestThemeColors.blueprint[200],
      surface: mdsHarvestThemeColors.graphite[100],
      ghost: mdsHarvestThemeColors.graphite[0],
    },
    background: {
      voice: {
        self: mdsHarvestThemeColors.blueprint[0],
        others: mdsHarvestThemeColors.graphite[100],
      },
      button: {
        contrast: {
          default: mdsHarvestThemeColors.graphite[900],
          hover: mdsHarvestThemeColors.graphite[800],
        },
        default: {
          default: mdsHarvestThemeColors.graphite[200],
          hover: mdsHarvestThemeColors.graphite[300],
        },
        chip: {
          default: mdsHarvestThemeColors.graphite[200],
          hover: mdsHarvestThemeColors.graphite[300],
          selectedNegative: mdsHarvestThemeColors.editor[0],
          selectedPositive: mdsHarvestThemeColors.successLight,
        },
        toggle: {
          default: mdsHarvestThemeColors.graphite[100],
          hover: mdsHarvestThemeColors.graphite[200],
          toggled: mdsHarvestThemeColors.blueprint[100],
          toggledHover: mdsHarvestThemeColors.blueprint[200],
        },
        onAccent: {
          default: "transparent",
          hover: mdsHarvestThemeColors.graphite[300],
        },
        emphasis: {
          default: mdsHarvestThemeColors.blueprint[300],
          hover: mdsHarvestThemeColors.blueprint[400],
        },
        emphasisLight: {
          default: mdsHarvestThemeColors.blueprint[100],
          hover: mdsHarvestThemeColors.blueprint[200],
        },
        transparent: {
          default: "transparent",
          hover: mdsHarvestThemeColors.graphite[200],
        },
        navigation: {
          default: "transparent",
          hover: mdsHarvestThemeColors.latte[100],
        },
        chrome: {
          default: mdsHarvestThemeColors.canvas[0],
          hover: mdsHarvestThemeColors.latte[100],
        },
        subtle: {
          default: mdsHarvestThemeColors.canvas[0],
          hover: mdsHarvestThemeColors.latte[100],
        },
        danger: {
          default: "transparent",
          hover: mdsHarvestThemeColors.editor[0],
        },
        dangerFilled: {
          default: mdsHarvestThemeColors.editor[0],
          hover: mdsHarvestThemeColors.editor[100],
        },
        brand: {
          default: mdsHarvestThemeColors.editor[300],
          hover: mdsHarvestThemeColors.editor[200],
        },
        tabBar: {
          active: mdsHarvestThemeColors.graphite[0],
          default: mdsHarvestThemeColors.graphite[100],
          hover: mdsHarvestThemeColors.graphite[200],
        },
        onChrome: {
          default: "transparent",
          hover: mdsHarvestThemeColors.latte[200],
        },
        floatingIcon: {
          default: mdsHarvestThemeColors.graphite[0],
          hover: mdsHarvestThemeColors.graphite[100],
        },
        contrastAccent: {
          default: mdsHarvestThemeColors.graphite[0],
          hover: mdsHarvestThemeColors.graphite[100],
        },
      },
      list: {
        default: mdsHarvestThemeColors.graphite[0],
        hover: mdsHarvestThemeColors.graphite[100],
        selected: mdsHarvestThemeColors.blueprint[100],
        deepsearch: mdsHarvestThemeColors.blueprint[0],
      },
      menu: {
        default: mdsHarvestThemeColors.graphite[0],
        hover: mdsHarvestThemeColors.graphite[100],
        selected: mdsHarvestThemeColors.graphite[200],
        emphasized: mdsHarvestThemeColors.blueprint[0],
      },
      input: {
        default: {
          default: mdsHarvestThemeColors.graphite[0],
          focused: mdsHarvestThemeColors.graphite[100],
        },
        chrome: {
          default: mdsHarvestThemeColors.canvas[0],
          focused: mdsHarvestThemeColors.latte[100],
          hover: mdsHarvestThemeColors.latte[100],
        },
        onAccent: {
          default: mdsHarvestThemeColors.graphite[100],
          focused: mdsHarvestThemeColors.graphite[0],
        },
        inline: {
          default: "transparent",
          focused: mdsHarvestThemeColors.graphite[200],
          hover: mdsHarvestThemeColors.graphite[200],
        },
      },
      message: {
        user: mdsHarvestThemeColors.graphite[100],
        mem: "transparent",
      },
      badge: {
        default: mdsHarvestThemeColors.graphite[200],
        emphasis: mdsHarvestThemeColors.blueprint[100],
        moreEmphasis: mdsHarvestThemeColors.blueprint[200],
        negative: mdsHarvestThemeColors.editor[0],
      },
      banner: {
        danger: mdsHarvestThemeColors.editor[0],
        emphasis: mdsHarvestThemeColors.blueprint[100],
        default: mdsHarvestThemeColors.graphite[200],
      },
      card: {
        emphasis: mdsHarvestThemeColors.blueprint[100],
        default: mdsHarvestThemeColors.graphite[100],
      },
      activeTab: mdsHarvestThemeColors.blueprint[300],
      illustration: {
        default: mdsHarvestThemeColors.latte[100],
        onAccent: mdsHarvestThemeColors.graphite[300],
        contrast: mdsHarvestThemeColors.graphite[0],
      },
      diff: mdsHarvestThemeColors.blueprint[0],
      skeleton: {
        bg: "#eee", // deliberately using non-themed color
        ends: mdsHarvestThemeColors.graphite[200],
        middle: mdsHarvestThemeColors.graphite[0],
      },
    },
    success: "#23B688",
  },

  shadows: {
    ...mdsCommonTheme.shadows,
    surface: "0 4px 6px 0 rgba(55, 55, 55, 0.10)",
  },
};
